<script setup lang="ts">
import { Field } from 'vee-validate'

import { ClipboardList, Gift, Smartphone, UserRound } from 'lucide-vue-next'

import CardTitle from '@/components/CardTitle.vue'
import InputCaracterCounter from '@/components/InputCaracterCounter.vue'
import MerchantTextFormal from '@/views/loyaltySupports/components/MerchantTextFormal.vue'
import PreviewZone from './PreviewZone.vue'
import { PREVIEW_ZONE_ATTRIBUTE } from '../composables/previewZone'

import type { LoyaltyMerchantFormText, LoyaltyMerchantFormTexts } from '@/types/global'
import type { AppearanceTab } from '../composables/webappPageTabs'

type HomeTextsFields = LoyaltyMerchantFormTexts['webappTextsFields']
type RegistrationTextsFields = LoyaltyMerchantFormTexts['webappRegistrationTextsFields']
type TextKey = keyof HomeTextsFields | keyof RegistrationTextsFields

interface TextEntry {
  key: TextKey
  field: LoyaltyMerchantFormText
  zone?: AppearanceTab
}

interface TextGroup {
  title: string
  icon: typeof Smartphone
  zone: AppearanceTab
  fields: TextEntry[]
}

const props = defineProps<{
  merchantText: LoyaltyMerchantFormTexts
}>()

const { t } = useI18n()

const home = (key: keyof HomeTextsFields): TextEntry => ({ key, field: props.merchantText.webappTextsFields[key] })

const registration = (key: keyof RegistrationTextsFields, zone?: AppearanceTab): TextEntry => ({
  key,
  field: props.merchantText.webappRegistrationTextsFields[key],
  zone
})

const groups = computed<TextGroup[]>(() => [
  {
    title: t('pages.loyaltySupports.webapp.texts.welcomeScreen'),
    icon: Smartphone,
    zone: 'home',
    fields: [
      home('webappHomeTitle'),
      home('webappHomeSubtitle'),
      registration('webappWelcomeButton'),
      registration('webappPhoneSubtitle', 'phone')
    ]
  },
  {
    title: t('pages.loyaltySupports.webapp.tabs.storeSelection'),
    icon: ClipboardList,
    zone: 'storeSelection',
    fields: [registration('webappStoreSelectionTitle'), registration('webappStoreSelectionLabel')]
  },
  {
    title: t('pages.loyaltySupports.webapp.tabs.profileInfo'),
    icon: UserRound,
    zone: 'profileInfo',
    fields: [registration('webappProfileTitle'), registration('webappProfileSubtitle')]
  },
  {
    title: t('pages.loyaltySupports.webapp.previewZones.optin'),
    icon: Gift,
    zone: 'optin',
    fields: [
      registration('webappOptinTitle'),
      registration('webappOptinSubtitle'),
      registration('webappOptinYes'),
      registration('webappOptinNo')
    ]
  }
])

const zoneAttribute = (zone?: AppearanceTab) => (zone ? { [PREVIEW_ZONE_ATTRIBUTE]: zone } : {})
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="rounded-xl border border-border bg-white p-5">
      <CardTitle class="mb-6" :title="t('pages.loyaltyPage.tabs.myTexts.formal.title')" />
      <MerchantTextFormal />
    </div>
    <PreviewZone v-for="group in groups" :key="group.title" :zone="group.zone">
      <h3 class="flex items-center gap-2 pr-40 text-base font-sans font-semibold">
        <component :is="group.icon" class="size-4 text-muted-foreground" />
        {{ group.title }}
      </h3>
      <div class="mt-5 grid grid-cols-6 gap-6">
        <div
          v-for="{ key, field, zone } in group.fields"
          :key="key"
          class="col-span-6 sm:col-span-3"
          v-bind="zoneAttribute(zone)"
        >
          <label :for="key" class="block text-sm font-medium text-foreground">{{ field.label }}</label>
          <Field v-slot="{ value, handleChange, handleBlur }" v-model="field.value" :name="key" :rules="field.rules">
            <Textarea
              :id="key"
              class="mt-1"
              :model-value="value"
              @update:model-value="handleChange"
              @blur="handleBlur"
            />
          </Field>
          <InputCaracterCounter
            v-if="field.maxLength"
            :content="field.value"
            :max-length="field.maxLength"
            @truncate-content="field.value = $event"
          />
        </div>
      </div>
    </PreviewZone>
  </div>
</template>
